import {
  AccountStatusVariant,
  LandlordVerificationStatus,
  LeaseVerificationStatus,
  PaymentStatuses,
  PendingIdentityVerificationStatus,
  RvStatus
} from "./constants";
import { Renter, RvStatusType, SelectValue, Verification } from "./types";
import { Subscriber } from "views/rent-reporting-id/types";

/**
 * `combined_search_v2` display status resolver.
 *
 * When the partner has the `combined_search_v2` feature, the rent reporting list
 * query returns a precomputed `account_status` per renter, so we read it directly
 * instead of deriving it from the granular verification/case fields.
 *
 * The backend emits `pending_idv` (lowercase) whereas the badge enum value is
 * `pending_IDV`, so that single value is normalized here.
 */
const getDisplayAccountStatusV2 = (
  accountStatus?: string
): AccountStatusVariant => {
  if (accountStatus === "pending_idv") return AccountStatusVariant.PendingIDV;
  return (accountStatus ??
    AccountStatusVariant.Pending) as AccountStatusVariant;
};

/**
 * Picks the right display-status resolver for the partner: the precomputed
 * `account_status` under `combined_search_v2`, the derived status otherwise.
 */
export const resolveDisplayAccountStatus = (
  renter: Renter | Subscriber,
  options: { isCombinedSearchV2: boolean }
): AccountStatusVariant =>
  options.isCombinedSearchV2
    ? getDisplayAccountStatusV2(renter.account_status)
    : getDisplayAccountStatus(renter);

const PENDING_ACCOUNT_STATUSES = [
  AccountStatusVariant.Pending,
  AccountStatusVariant.PendingIDV,
  AccountStatusVariant.PendingLeaseVerification,
  AccountStatusVariant.PendingLandlordVerification,
  AccountStatusVariant.PendingOptIn,
  AccountStatusVariant.PendingOptOut
];

/**
 * True when a display account status is `pending` or any of its refined
 * pending sub-states. Under `combined_search_v2` the backend returns the
 * refined status directly in `account_status`, so a plain `=== Pending`
 * comparison would miss e.g. `pending_IDV`.
 */
export const isPendingAccountStatus = (
  status?: AccountStatusVariant
): boolean => status !== undefined && PENDING_ACCOUNT_STATUSES.includes(status);

const getDisplayAccountStatus = (
  renter: Renter | Subscriber
): AccountStatusVariant => {
  // Coarse status lives in `type` on the Renter (list) shape and in
  // `account_status` on the Subscriber (detail) shape.
  const renterType = (
    "type" in renter ? renter.type : renter.account_status
  ) as AccountStatusVariant;

  if (renterType === AccountStatusVariant.Pending) {
    if (renter.case_status === AccountStatusVariant.PendingOptIn)
      return AccountStatusVariant.PendingOptIn;

    if (renter.case_status === AccountStatusVariant.PendingOptOut)
      return AccountStatusVariant.PendingOptOut;

    // Granular verification statuses exist on both the Renter (list) shape and
    // the Subscriber (detail) shape — on Subscriber they are optional, so they
    // may be undefined, in which case the comparisons fall through to `Pending`.
    if (
      (
        [
          PendingIdentityVerificationStatus.Pending,
          PendingIdentityVerificationStatus.Rejected
        ] as (PendingIdentityVerificationStatus | undefined)[]
      ).includes(renter.identity_verification_status)
    )
      return AccountStatusVariant.PendingIDV;
    if (
      (
        [LeaseVerificationStatus.Pending, LeaseVerificationStatus.Rejected] as (
          | LeaseVerificationStatus
          | undefined
        )[]
      ).includes(renter.lease_verification_status)
    )
      return AccountStatusVariant.PendingLeaseVerification;
    if (
      (
        [
          LandlordVerificationStatus.Pending,
          LandlordVerificationStatus.Rejected
        ] as (LandlordVerificationStatus | undefined)[]
      ).includes(renter.landlord_verification_status)
    )
      return AccountStatusVariant.PendingLandlordVerification;
  }

  return renterType;
};

/**
 * A select value holds either a payment status or a numeric months-delinquency
 * option in `action`; normalize it into the verification payload fields.
 */
export const toVerificationPayload = (
  action: SelectValue["action"]
): Pick<Verification, "action" | "months_delinquency"> => {
  if (action === PaymentStatuses.PAID_ON_TIME)
    return {
      action: PaymentStatuses.PAID_ON_TIME,
      months_delinquency: undefined
    };
  if (action === PaymentStatuses.NO_DATA)
    return { action: PaymentStatuses.NO_DATA, months_delinquency: undefined };
  return { action: PaymentStatuses.DELINQUENCY, months_delinquency: action };
};

export function getRvStatusText(status: RvStatusType | null): string {
  if (!status) return "--";
  switch (status.status) {
    case RvStatus.Verified:
    case RvStatus.PendingConfirmation:
      if (status.months_delinquency === 0) return "Paid on-time";
      else return `${status.months_delinquency} + months late`;
    default:
      return "No data";
  }
}
